import React, { useEffect } from 'react'
import { Snackbar, Alert } from '@mui/material'
import { useAppSelector, useAppDispatch } from '../store'
import { selectNotifications, removeNotification } from '../features/ui/uiSlice'

/**
 * Global notification system driven by the Redux `uiSlice` notification queue.
 *
 * Renders one notification at a time (no stacking) as a top-center MUI Snackbar.
 * Auto-dismisses after the notification's `duration` (default 6 s).
 * Mount once in the app shell (e.g., inside `UserLayout` or `App`).
 *
 * For component-scoped notifications use the `useNotification` hook instead.
 */
const NotificationSystem: React.FC = () => {
  const dispatch = useAppDispatch()
  const notifications = useAppSelector(selectNotifications)

  // Show only the first notification (prevent stacking)
  const currentNotification = notifications[0]

  // Auto-dismiss after duration
  useEffect(() => {
    if (currentNotification && currentNotification.duration) {
      const timer = setTimeout(() => {
        dispatch(removeNotification(currentNotification.id))
      }, currentNotification.duration)

      return () => clearTimeout(timer)
    }
    return undefined
  }, [currentNotification, dispatch])

  const handleClose = () => {
    if (currentNotification) {
      dispatch(removeNotification(currentNotification.id))
    }
  }

  if (!currentNotification) {
    return null
  }

  return (
    <Snackbar
      open={true}
      autoHideDuration={currentNotification.duration || 6000}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
      <Alert onClose={handleClose} severity={currentNotification.type} variant='filled' sx={{ width: '100%' }}>
        {currentNotification.message}
      </Alert>
    </Snackbar>
  )
}

export default NotificationSystem
